import {AgentListItems} from '@github-ui/agent-sessions/components/new-task-chat-input/AgentListItems'
import {useTopLevelAgents} from '@github-ui/agent-sessions/hooks/useTopLevelAgents'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {FileCheckIcon} from '@github-ui/pacer/CustomIcon'
import {CopilotIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, LinkButton} from '@primer/react'
import {useMemo} from 'react'

import {useCustomAgentsQuery} from '../../hooks/use-custom-agents-query'
import styles from './AgentSelector.module.css'
import {IconButtonSelector} from './IconButtonSelector'

type AgentSelectorProps = {
  nwo: string | undefined
  branch: string | undefined
  selectedAgent: string | undefined
  onSelect: (agent: string | undefined) => void
}

export function AgentSelector({nwo, branch, selectedAgent, onSelect}: AgentSelectorProps) {
  const {data: customAgents = [], isLoading: isLoadingCustom} = useCustomAgentsQuery(nwo)
  const use3pAgentInfo = isFeatureEnabled('copilot_3p_agent_info')
  const {data: topLevelAgents = [], isLoading: isLoadingTopLevel} = useTopLevelAgents(nwo, use3pAgentInfo)

  // Default to "Copilot" if feature flag is enabled and no agent is selected
  const effectiveSelectedAgent = use3pAgentInfo && !selectedAgent ? 'Copilot' : selectedAgent

  const isLoading = isLoadingCustom || isLoadingTopLevel
  const agents = [...topLevelAgents, ...customAgents]
  const selectedAgentObj = agents.find(agent => agent.name === effectiveSelectedAgent)
  const selectedAgentName = selectedAgentObj?.display_name || selectedAgentObj?.name

  const newFileUrl = useMemo(() => {
    const params = new URLSearchParams({
      filename: '.github/agents/my-agent.agent.md',
      value:
        '---\n# Fill in the fields below to create a basic custom agent for your repository.\n# The Copilot CLI can be used for local testing: https://gh.io/customagents/cli\n# To make this agent available, merge this file into the default repository branch.\n# For format details, see: https://gh.io/customagents/config\n\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here...',
    })
    return `/${nwo}/new/${branch}?${params.toString()}`
  }, [nwo, branch])

  const ariaLabel = selectedAgentName
    ? `${use3pAgentInfo ? 'Agent' : 'Custom agent'}: ${selectedAgentName}`
    : `Select ${use3pAgentInfo ? 'an agent' : 'a custom agent'}`

  const disabled = !nwo || !branch || isLoading

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButtonSelector
          icon={CopilotIcon}
          selectedIcon={FileCheckIcon}
          aria-label={ariaLabel}
          selected={Boolean(selectedAgentObj)}
          disabled={disabled}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        {topLevelAgents.length === 0 && customAgents.length === 0 ? (
          <EmptyState newFileUrl={newFileUrl} />
        ) : (
          <ActionList>
            {!use3pAgentInfo && (
              <ActionList.Group selectionVariant="single">
                <ActionList.Item selected={!selectedAgent} onSelect={() => onSelect(undefined)}>
                  None
                </ActionList.Item>
              </ActionList.Group>
            )}

            <AgentListItems
              topLevelAgents={topLevelAgents}
              customAgents={customAgents}
              selectedAgent={effectiveSelectedAgent}
              onSelectAgent={() => onSelect(undefined)} // TODO: onSelect should handle selecting top-level agents
              onSelectCustomAgent={(agentName: string) => onSelect(agentName)}
              showTopLevelAgents={use3pAgentInfo}
            />

            <ActionList.Divider />

            <ActionList.Group selectionVariant={false}>
              <ActionList.LinkItem href={newFileUrl} target="_blank">
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Create a custom agent
              </ActionList.LinkItem>
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
const EmptyState = ({newFileUrl}: {newFileUrl: string}) => (
  <div className={styles.emptyStateContainer}>
    <h3 className={styles.emptyStateTitle}>This repository has no custom agents</h3>
    <p className={styles.emptyStateDescription}>
      Custom agents are reusable instructions and tools in your repository.
    </p>
    <LinkButton href={newFileUrl}>Create a custom agent</LinkButton>
  </div>
)

try{ AgentSelector.displayName ||= 'AgentSelector' } catch {}
try{ EmptyState.displayName ||= 'EmptyState' } catch {}